import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select, { components } from 'react-select';
import Button from 'components/Button/Button';
import styles from './ExportCheckInModal.module.scss';
import {
  PRESET_OPTIONS,
  getDefaultDateRange,
  formatDateForAPI,
} from 'utils/datePresets';
import { Box } from '@mantine/core';
import { modals } from '@mantine/modals';

interface ExportCheckInModalProps {
  onExport: (startDate: string, endDate: string) => void;
}

// Custom Option component to match Fig 3 layout - label on left, date range on right
const CustomOption = (props: any) => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <div className={styles.optionContent}>
        <span className={styles.optionLabel}>{data.label}</span>
        <span className={styles.optionDateRange}>{data.dateRange}</span>
      </div>
    </components.Option>
  );
};

// Custom DropdownIndicator to show simple arrow
const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <span style={{ fontSize: '12px', color: '#333' }}>▼</span>
    </components.DropdownIndicator>
  );
};

const ExportCheckInModal: React.FC<ExportCheckInModalProps> = ({
  onExport,
}) => {
  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState<Date>(defaultRange.startDate);
  const [endDate, setEndDate] = useState<Date>(defaultRange.endDate);

  // Default to "last_30_days" as shown in Fig 2
  const [selectedPreset, setSelectedPreset] = useState<string>('last_30_days');

  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  const formatDisplayDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPresetDateRange = (presetValue: string): string => {
    const preset = PRESET_OPTIONS.find(p => p.value === presetValue);
    if (preset) {
      const { startDate, endDate } = preset.getDateRange();
      const startStr = `${startDate.getMonth() + 1}.${startDate.getDate()}.${startDate.getFullYear()}`;
      const endStr = `${endDate.getMonth() + 1}.${endDate.getDate()}.${endDate.getFullYear()}`;
      return `${startStr} - ${endStr}`;
    }
    return '';
  };

  // Transform preset options for react-select with date ranges
  const selectOptions = PRESET_OPTIONS.map(preset => ({
    value: preset.value,
    label: preset.label,
    dateRange: getPresetDateRange(preset.value)
  }));

  // Find currently selected option for react-select
  const selectedOption = selectOptions.find(opt => opt.value === selectedPreset) || null;

  const handlePresetChange = (option: any) => {
    if (option) {
      setSelectedPreset(option.value);

      const preset = PRESET_OPTIONS.find(p => p.value === option.value);
      if (preset) {
        const { startDate: newStart, endDate: newEnd } = preset.getDateRange();
        setStartDate(newStart);
        setEndDate(newEnd);
      }
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setStartDate(date);
      setSelectedPreset(''); // Clear preset when manually changing dates
      setShowStartPicker(false);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setEndDate(date);
      setSelectedPreset(''); // Clear preset when manually changing dates
      setShowEndPicker(false);
    }
  };

  const handleExport = () => {
    const startDateStr = formatDateForAPI(startDate);
    const endDateStr = formatDateForAPI(endDate);
    onExport(startDateStr, endDateStr);
  };

  return (
    <Box className={styles.exportModalWrapper}>
      <style>{`
        [data-mantine-stop-propagation] button[aria-label="Close modal"] {
          display: none !important;
        }
      `}</style>
      <div className={styles.header}>
        <h2 className={styles.title}>Export Check-In CSV</h2>
        <button className={styles.closeBtn} onClick={() => modals.closeAll()}>×</button>
      </div>

      <div className={styles.modalContent}>
        <div className={styles.topSection}>
          <p className={styles.labelText}>between</p>

          <div className={styles.dateClickable} onClick={() => setShowStartPicker(!showStartPicker)}>
            {formatDisplayDate(startDate)}
          </div>
          {showStartPicker && (
            <div className={styles.datePickerWrapper}>
              <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                maxDate={endDate}
                inline
              />
            </div>
          )}

          <p className={styles.labelText}>to</p>

          <div className={styles.dateClickable} onClick={() => setShowEndPicker(!showEndPicker)}>
            {formatDisplayDate(endDate)}
          </div>
          {showEndPicker && (
            <div className={styles.datePickerWrapper}>
              <DatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                minDate={startDate}
                maxDate={new Date()}
                inline
              />
            </div>
          )}

          <p className={styles.labelText}>or choose preset</p>

          {/* react-select dropdown with custom Option renderer */}
          <div className={styles.presetSelectWrapper}>
          <Select
            value={selectedOption}
            onChange={handlePresetChange}
            options={selectOptions}
            isSearchable={false}  // CRITICAL: Disable text input cursor
            components={{
              Option: CustomOption,
              DropdownIndicator,
              IndicatorSeparator: () => null // Remove separator line
            }}
            placeholder="Last 30 days"
            menuPlacement="auto"
            styles={{
              control: (base, state) => ({
                ...base,
                border: '1px solid #ccc',
                borderRadius: '4px',
                minHeight: '32px',
                fontSize: '12px',
                boxShadow: 'none',
                cursor: 'pointer',
                paddingLeft: '4px',
                '&:hover': {
                  borderColor: '#999'
                }
              }),
              singleValue: (base) => ({
                ...base,
                color: '#000',
                fontWeight: 600,
                fontSize: '12px',
                textAlign: 'left'
              }),
              placeholder: (base) => ({
                ...base,
                color: '#666',
                fontSize: '12px',
                textAlign: 'left'
              }),
              valueContainer: (base) => ({
                ...base,
                padding: '0 8px',
                justifyContent: 'flex-start'
              }),
              menu: (base) => ({
                ...base,
                borderRadius: '4px',
                marginTop: '4px',
                marginBottom: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                border: '1px solid #ddd'
              }),
              menuList: (base) => ({
                ...base,
                paddingTop: '8px',
                paddingBottom: 0,
                borderRadius: '4px',
                maxHeight: 'none'
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? '#e8f4fb'
                  : (state.isFocused ? '#f5f5f5' : 'white'),
                cursor: 'pointer',
                padding: '8px 12px',
                fontSize: '12px',
                '&:active': {
                  backgroundColor: '#e8f4fb'
                }
              })
            }}
          />
          </div>
        </div>

        <div className={styles.bottomSection}>
          <Button
            onClick={handleExport}
            width="100%"
            color="#547A8F"
            borderRadius="8px"
            borderColor="transparent"
            textColor="white"
          >
            Get Check-ins CSV
          </Button>
        </div>
      </div>
    </Box>
  );
};

export default ExportCheckInModal;
